/* eslint-disable no-param-reassign */
import { RefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import { createPortal } from 'react-dom';
import useAiReviewerRole from 'common/hooks/useAiReviewerRole';
import useCanClearCapTable from 'common/hooks/useCanClearCapTable';
import { CellChange } from 'components/Spreadsheet/Spreadsheet.types';
import { IWorkbook } from 'models/interfaces/spreadsheet-types/workbook-types';
import AiProcessingMenu from './AiProcessingMenu';
import GenerateCapTableJob from './GenerateCapTableJob';
import {
  AcceptAIFieldRequest,
  AIEvidenceResetRequest,
  AIReviewBatchRequest,
  ApiCreateDocumentGenerationJob,
  ApiService,
  CapTablesService,
  CompanyService,
  DocumentReferencesView,
  SecurityModel,
} from '../../../../../api';
import { SECURITY_NAME_ALIAS } from '../../../../../common/constants/cap-table/capTable';
import { useStore } from '../../../../../common/store';
import { UseStoreValues } from '../../../../../common/types/store';
import { HandlePushingAttributeMappingsPayload } from '../../../../../components/Drawer/attribute-mapped-widget-drawer/attribute-mapped-widget-drawer.types';
import { AttributeMappedWidgetDrawer } from '../../../../../components/Drawer/attribute-mapped-widget-drawer/AttributeMappedWidgetDrawer';
import { CitationPushedPayload } from '../../../../../components/Drawer/attribute-mapped-widget-drawer/AttributeMappedWidgetDrawerContext';
import WidgetDrawerProps from '../../../../../components/Drawer/widget-drawer/WidgetDrawer.props';
import { EventType, useEventBus, useEventSubscription } from '../../../../../context';
import { useAsyncProcessor } from '../../../../../context/AsyncProcessorContext/AsyncProcessorContext';
import { DocumentReference, useDocumentFootnotes } from '../../../../../context/DocumentFootnotesContext';
import { NavigateToDocumentReferencePayload } from '../../../../../context/EventBusContext/EventBusContext.type';
import { useLayoutContext } from '../../../../../context/LayoutContext';
import CapatableService from '../../../../../services/captable';
import { createCapTableGenerationOnResult } from '../../../../../services/capTableGenerationResult';
import CitationService from '../../../../../services/citations';
import { CAP_TABLE_REPROCESS_JOB_TITLE } from '../../../../../services/generationJobs';
import { useGetCaptableInfo, usePatchCapTableSecurities } from '../../../../../services/hooks/captable';
import { useGetDocumentsById } from '../../../../../services/hooks/documents';
import { CapTableReferenceGenerator, MappingReferenceData } from '../../../../../services/reference-generator';
import { ClearAttributeMappingsPayload } from '../../../../../services/sheet-attribute-manager/models/sheet-attribute-manager.interface';
import { useCellMappingStore, usePendingReviewCount, useReviewPaneCollapsed, useReviewStore } from '../../../../../store';
import { useAttributeMapperStore } from '../../../../../store/useAttributeMapperStore';
import type { ReviewItem } from '../../../../../store/useReviewStore';
import { extractResponseErrorMessage, Logger, removeClassName } from '../../../../../utilities';
import { AttributeMappingType } from '../../../../Financials/financials-widget-drawer/types';
import { rowConfig } from '../../data';
import { runCapTableClear } from '../../utilities/capTableClear';
import { CAP_TABLE_RESET_API_MODE, type CapTableResetMode, capTableResetSummary } from '../../utilities/capTableReset';
import { applyProposedExcelReferences } from '../../utilities/excelReferenceBoundary';
import { addManualCitation, type ManualCitationMode, shouldOfferReplace } from '../../utilities/manualCitationAdd';
import {
  type CellCitationPlan,
  cellHasPendingSuggestion,
  cellsHoldingCitationIds,
  citationCellId,
  type CitationTargetCell,
  citationTargetRejection,
  pendingReviewItemsForSelection,
  persistedCitationIdsFromPlans,
  planApplyCitationToCells,
  planClearCitationsOnCells,
  restrictClearPlansToIds,
  reviewCellIdsForSelection,
} from '../../utilities/multiCellCitationActions';
import { resolveAcceptValue } from '../../utilities/resolveAiOptionValue';
import { aggregateReviewItems, pickActiveCitation } from '../../utilities/reviewAggregator';
import {
  buildBulkItems,
  deletedSecurityIdsOf,
  partitionAcceptableItems,
  summarizeBulkResults,
} from '../../utilities/reviewBulk';
import {
  acceptRefsForFields,
  applyAcceptToSecurity,
  applyDismissToSecurity,
  applyReopenToSecurity,
  applySettledFieldsToSecurities,
  declineRefsForFields,
  isAiCreatedCitation,
  removeCitationFromSecurities,
  reopenRefsForFields,
  resolveDeclineRevert,
} from '../../utilities/securityAiPatches';
import { settleCitationReview } from '../../utilities/settleCitationReview';
import { isMappingSettleItem, settleMappingReview } from '../../utilities/settleMappingReview';
import useGridCitationSelectionStore from '../../utilities/useGridCitationSelectionStore';
import useSelectionGuard from '../../utilities/useSelectionGuard';
import { useAIFieldAcceptContext } from '../captable-table/AIFieldAcceptContext';
import { CapTableCellWithReferences } from '../captable-table/CaptableTable.props';
import { useCitationContext } from '../captable-table/CitationContext';
import { CreateCapTableCitationsDialog, useCreateCapTableCitationsAction } from '../create-citations';
import GridCitationSelectionToolbar from '../grid-citation-selection/GridCitationSelectionToolbar';
import { PendingReviewBadge, ReviewSidePanel } from '../review-side-panel';
import {
  buildCellExcelJump,
  buildReviewDocJump,
  buildReviewExcelJump,
  clearCellSourceReveal,
  revealCellCitationSource,
} from '../review-side-panel/reviewDocJump';
import type { ReviewBulkResult } from '../review-side-panel/ReviewSidePanel.props';

// Ctrl+Shift-click (Cmd+Shift-click on Mac) selects a cell for the citation actions.
const isCitationSelectClick = (event: MouseEvent) =>
  event.button === 0 && (event.ctrlKey || event.metaKey) && event.shiftKey;

export interface CapTableWidgetDrawerProps extends WidgetDrawerProps {
  workbook: IWorkbook;
  onTableChange: (cell: any, value: number) => void;
  captableSetProgrammaticUpdate?: (isProgrammatic: boolean) => void;
  /** The version selector's own id; unlike the store's cap table it survives a reload. */
  selectedVersionId?: string | number | null;
  /** Grid whose cells Ctrl+Shift+click selects. */
  gridContainerRef?: RefObject<HTMLElement | null>;
  /** Navigation element the citation selection toolbar renders into. */
  gridSelectionToolbarHost?: HTMLElement | null;
}

const CapTableWidgetDrawer = ({
  onTableChange,
  workbook,
  spreadsheetNotesProps,
  notesProps,
  documentReferencesProps,
  applicationContext,
  documentApplicationStorageKey,
  captableSetProgrammaticUpdate,
  selectedVersionId,
  gridContainerRef,
  gridSelectionToolbarHost,
}: CapTableWidgetDrawerProps) => {
  const [storeValue] = useStore() as unknown as UseStoreValues;
  const { captableInfo, companyInfo } = storeValue;
  // A name acceptance can rebuild the table while citation review is awaiting
  // the server. Always settle onto the current store and cells after that await.
  const latestReviewState = useRef({ captableInfo, workbook });
  latestReviewState.current = { captableInfo, workbook };
  const { currentMeasurementDate } = useLayoutContext();
  const { getCellMapping } = useCellMappingStore();
  const { attributeMapperManager, getAttributeMapperManager, clearAttributeMapperManager } = useAttributeMapperStore();
  const { registerFootnote, clearFootnotes } = useDocumentFootnotes();
  const { addJob, removeJob } = useAsyncProcessor();
  const { acceptField, dismissField, dismissAiColumn } = useAIFieldAcceptContext();
  const setReviewItems = useReviewStore(s => s.setReviewItems);
  const resetReviewSession = useReviewStore(s => s.resetReviewSession);
  // Pending-only count: reviewed items still live in the store (for the muted
  // Reviewed section), so the full length would keep the drawer auto-opening.
  const pendingReviewCount = usePendingReviewCount();
  // Withhold the side panel when the visibility selector marks it collapsed.
  const reviewPaneCollapsed = useReviewPaneCollapsed();
  const setPaneCollapsed = useReviewStore(s => s.setPaneCollapsed);
  const resetReviewFilter = useReviewStore(s => s.resetReviewFilter);
  // SCAL-9335: the AI review experience is analyst-side only. Non-reviewers
  // (clients / view-only roles) get the plain cap table: no review panel or
  // badge, no auto-open, no review items - and the stamped store flag below
  // keeps the grid viewers plain (normal blue citations, no AI hue).
  const isAiReviewer = useAiReviewerRole();
  const setReviewerMode = useReviewStore(s => s.setReviewerMode);
  useEffect(() => {
    setReviewerMode(isAiReviewer);
  }, [isAiReviewer, setReviewerMode]);
  const patchSecurities = usePatchCapTableSecurities();
  const [, getCapTableInfo] = useGetCaptableInfo();
  const capTableService = useMemo(() => new CapatableService(), []);
  const citationService = useMemo(() => new CitationService(), []);
  const { setActiveCitation } = useCitationContext();

  const { publish } = useEventBus();
  const [citation, setCitation] = useState<(CitationPushedPayload & { documentReferenceId: number }) | undefined>();
  const gridSelectionMode = useGridCitationSelectionStore(s => s.mode);
  const gridSelected = useGridCitationSelectionStore(s => s.selected);
  // While cells are being selected, plain clicks neither jump to a source nor place a citation.
  const selectingCells = () => useGridCitationSelectionStore.getState().mode === 'add-multiple';
  const setFilterCellIds = useReviewStore(s => s.setFilterCellIds);
  // SCAL-8970: the cell awaiting an explicit Append-vs-Replace choice; non-null
  // keeps the confirm dialog open while the pushed citation stays pending.
  const [replacePromptCell, setReplacePromptCell] = useState<CapTableCellWithReferences | null>(null);
  const [gridCitationBusy, setGridCitationBusy] = useState(false);
  const gridCitationBusyRef = useRef(false);
  const [generateDialogOpen, setGenerateDialogOpen] = useState(false);
  const [createCitationsEnabled, setCreateCitationsEnabled] = useState(false);
  const createCitationsAction = useCreateCapTableCitationsAction({ enabled: createCitationsEnabled });
  const [reprocessDialogOpen, setReprocessDialogOpen] = useState(false);
  const [reprocessJobMetadata, setReprocessJobMetadata] = useState<{
    document_reference_id: number;
    measurement_date_id?: number | null;
    file_id?: number;
  } | null>(null);
  // Helper function to collect and register citation footnotes from cap table cells
  const registerCapTableCitationFootnotes = () => {
    if (!workbook?.captable?.cells) {
      return;
    }

    const cellsWithCitations = Object.values(workbook.captable.cells as CapTableCellWithReferences[]).filter(
      cell => cell.citationReferences && cell.citationReferences.length > 0
    );

    if (cellsWithCitations.length === 0) {
      clearFootnotes();
      return;
    }

    // Group citations by cell to create unique footnote IDs
    cellsWithCitations.forEach(cell => {
      const cellKey = cell.key || `${cell.columnLegend}${cell.rowNumber}`;

      if (!cell.citationReferences) {
        return;
      }

      // Convert CitationReferenceData to DocumentReference format
      const documentReferencesMap = new Map<number, DocumentReference>();

      cell.citationReferences.forEach(citationRef => {
        const docId = citationRef.document_reference;

        if (!documentReferencesMap.has(docId)) {
          documentReferencesMap.set(docId, {
            documentId: docId,
            fields: [],
          });
        }

        const docRef = documentReferencesMap.get(docId)!;

        // For citations, we use the field_name and create pseudo cell references from token positions
        const existingFieldIndex = docRef.fields.findIndex(f => f.fieldName === citationRef.field_name);

        const cellReferences = citationRef.tokenReferences.map(tokenRef => ({
          ref: `p${tokenRef.page}-${tokenRef.token_ids.join(',')}`,
          direction: 'positive' as const,
        }));

        if (existingFieldIndex >= 0) {
          // Merge cell references into existing field
          docRef.fields[existingFieldIndex].cellReferences.push(...cellReferences);
        } else {
          // Add new field entry
          docRef.fields.push({
            fieldName: citationRef.field_name,
            sheetName: 'Citation', // Citations don't have sheet names, use a placeholder
            cellReferences,
          });
        }
      });

      // Register footnote for this cell
      registerFootnote(`captable-cell-${cellKey}`, Array.from(documentReferencesMap.values()));
    });
  };

  // Activating a grid cell reveals its source. A CITED cell (SCAL-9182) is the
  // reverse of the card-to-cell link: select its review card - opening the pane and
  // drawer if hidden - and jump the doc view to the citation, exactly like the
  // card's eyeball. Uncited cells keep the pre-existing Excel-mapping navigation.
  // While a pushed citation is pending, the attach subscription below owns the click.
  useEventSubscription(
    EventType.SPREADSHEET_ACTIVE_CELL_CHANGED,
    ([newCell]: [{ cellInfo: CapTableCellWithReferences }]) => {
      if (citation || selectingCells()) {
        return;
      }
      const cell = newCell.cellInfo;
      const navigate = (payload: NavigateToDocumentReferencePayload) =>
        publish(EventType.NAVIGATE_TO_DOCUMENT_REFERENCE, payload);
      if (revealCellCitationSource(cell, setActiveCitation, navigate)) {
        return;
      }
      clearCellSourceReveal(setActiveCitation);
      const excelJump = buildCellExcelJump(cell);
      if (excelJump) {
        navigate(excelJump.navigatePayload);
      }
    }
  );

  // Finish a pending manual-citation add on `cell` (SCAL-8970): merge into the
  // same-document entry or append a human-stamped one; 'replace' additionally
  // flags the new entry so the save soft-dismisses the cell's AI citations.
  const completeManualCitationAdd = (cell: CapTableCellWithReferences, mode: ManualCitationMode) => {
    if (!citation) {
      return;
    }

    cell.citationReferences = addManualCitation(
      cell.citationReferences,
      {
        fieldName: cell.alias,
        documentReferenceId: citation.documentReferenceId,
        tokenReference: {
          token_ids: citation.tokenIds,
          page: citation.page,
          plain_text: citation.selectedText,
          notes: [],
        },
      },
      mode
    );

    onTableChange(cell, cell.value as number);
    setCitation(undefined);
    setReplacePromptCell(null);

    enqueueSnackbar(`Citation added to ${cell.alias}(${cell.key})`, { variant: 'success' });

    registerCapTableCitationFootnotes();
  };

  const capTableCells = useCallback(
    (): CapTableCellWithReferences[] =>
      Object.values((workbook?.captable?.cells ?? {}) as Record<string, CapTableCellWithReferences>),
    [workbook?.captable?.cells]
  );

  // The selection gesture is taken at window capture and swallowed there, so the grid never
  // sees it and its own highlighted range cannot move or spread.
  useEffect(() => {
    if (!isAiReviewer || !gridContainerRef) {
      return undefined;
    }
    let swallowing = false;
    const swallow = (event: Event) => {
      event.preventDefault();
      event.stopPropagation();
    };
    const gridCellAt = (event: MouseEvent) => {
      const td = event.target instanceof Element ? event.target.closest<HTMLElement>('td') : null;
      return td && gridContainerRef.current?.contains(td) ? td : null;
    };
    const onMouseDown = (event: MouseEvent) => {
      const td = isCitationSelectClick(event) ? gridCellAt(event) : null;
      swallowing = td !== null;
      if (!td) {
        return;
      }
      swallow(event);
      const { columnId, alias } = td.dataset;
      const cell: CitationTargetCell = capTableCells().find(
        entry => String(entry.columnId) === columnId && entry.alias === alias
      ) ?? { isLegend: td.classList.contains('legend'), columnId, alias };
      const reason = citationTargetRejection(cell);
      const id = citationCellId(cell);
      if (reason || !id || cell.alias == null || cell.columnId == null) {
        enqueueSnackbar(reason ?? 'This cell is not a security field', { variant: 'error' });
        return;
      }
      const selection = useGridCitationSelectionStore.getState();
      if (selection.mode !== 'add-multiple') {
        selection.enterAddMultiple();
      }
      selection.toggle({ id, alias: cell.alias, columnId: cell.columnId, key: cell.key });
      // Deselecting the last cell also lifts the review filter it put on that cell, leaving any
      // other filtered cells in place.
      const { filterCellIds } = useReviewStore.getState();
      if (useGridCitationSelectionStore.getState().selected.length === 0 && filterCellIds.has(id)) {
        setFilterCellIds([...filterCellIds].filter(filterId => filterId !== id));
      }
    };
    const onFollowUp = (event: MouseEvent) => {
      if (swallowing) {
        swallow(event);
        swallowing = event.type !== 'click';
      }
    };
    window.addEventListener('mousedown', onMouseDown, true);
    window.addEventListener('mouseup', onFollowUp, true);
    window.addEventListener('click', onFollowUp, true);
    return () => {
      window.removeEventListener('mousedown', onMouseDown, true);
      window.removeEventListener('mouseup', onFollowUp, true);
      window.removeEventListener('click', onFollowUp, true);
    };
  }, [isAiReviewer, gridContainerRef, capTableCells, setFilterCellIds]);

  const selectedCapTableCells = () => {
    const byId = new Map(capTableCells().map(cell => [citationCellId(cell), cell]));
    return gridSelected
      .map(item => byId.get(item.id))
      .filter((cell): cell is CapTableCellWithReferences => Boolean(cell));
  };

  const persistCitationPlans = (plans: CellCitationPlan[]) => {
    const mutated = plans.filter(plan => plan.mutated);
    if (mutated.length === 0) {
      return 0;
    }
    captableSetProgrammaticUpdate?.(true);
    try {
      mutated.forEach(({ cell, citationReferences, mappingReferences }) => {
        const live = cell as CapTableCellWithReferences;
        live.citationReferences = citationReferences;
        if (mappingReferences !== undefined) {
          live.mappingReferences = mappingReferences;
          if (mappingReferences.length === 0 && citationReferences.length === 0) {
            live.columnarReferences = [];
            live.columnarReferencesMetadata = [];
            live.className = removeClassName(live.className ?? '', 'cell-with-references');
          }
        }
        onTableChange(live, live.value as number);
      });
    } finally {
      captableSetProgrammaticUpdate?.(false);
    }
    setReviewItems(aggregateReviewItems(workbook.captable.cells as Record<string, CapTableCellWithReferences>));
    registerCapTableCitationFootnotes();
    return mutated.length;
  };

  // Runs one toolbar action at a time, then leaves selection mode.
  const runGridSelectionAction = async (action: () => Promise<void>) => {
    if (!isAiReviewer || gridCitationBusyRef.current) {
      return;
    }
    gridCitationBusyRef.current = true;
    setGridCitationBusy(true);
    try {
      await action();
    } finally {
      gridCitationBusyRef.current = false;
      setGridCitationBusy(false);
      useGridCitationSelectionStore.getState().exit();
    }
  };

  const handleGridSelectionClear = () =>
    runGridSelectionAction(async () => {
      const cells = selectedCapTableCells();
      const plans = planClearCitationsOnCells(cells);
      if (plans.every(plan => !plan.mutated)) {
        enqueueSnackbar('No citations to clear', { variant: 'info' });
        return;
      }
      const persistIds = persistedCitationIdsFromPlans(plans);
      const results = await Promise.allSettled(persistIds.map(id => citationService.deleteCitation(id)));
      const succeeded = new Set(persistIds.filter((_, index) => results[index].status === 'fulfilled'));
      const failed = new Set(persistIds.filter(id => !succeeded.has(id)));

      const count = persistCitationPlans(restrictClearPlansToIds(cells, succeeded));
      if (succeeded.size > 0) {
        patchSecurities(securities =>
          [...succeeded].reduce((next, id) => removeCitationFromSecurities(next, id) as typeof next, securities)
        );
      }
      cellsHoldingCitationIds(cells, failed).forEach(cell => {
        enqueueSnackbar(`Failed to clear citations on ${cell.alias}(${cell.key})`, { variant: 'error' });
      });
      if (failed.size === 0) {
        enqueueSnackbar(`Cleared citations and references on ${count} cell${count === 1 ? '' : 's'}`, {
          variant: 'success',
        });
      }
    });

  const handleGridSelectionAttach = () => {
    if (!citation) {
      enqueueSnackbar('Select a citation in the document first', { variant: 'error' });
      return;
    }
    const { applied, rejected } = planApplyCitationToCells(selectedCapTableCells(), {
      documentReferenceId: citation.documentReferenceId,
      tokenReference: {
        token_ids: citation.tokenIds,
        page: citation.page,
        plain_text: citation.selectedText,
        notes: [],
      },
    });
    const count = persistCitationPlans(applied);
    rejected.forEach(item => enqueueSnackbar(item.reason, { variant: 'error' }));
    if (count > 0) {
      enqueueSnackbar(`Citation added to ${count} cell${count === 1 ? '' : 's'}`, { variant: 'success' });
      setCitation(undefined);
    }
    useGridCitationSelectionStore.getState().exit();
  };

  const handleGridSelectionFilter = () => {
    const ids = reviewCellIdsForSelection(selectedCapTableCells());
    setFilterCellIds(ids);
    setPaneCollapsed(false);
    enqueueSnackbar(
      ids.length > 0
        ? `Review filtered to ${ids.length} cell${ids.length === 1 ? '' : 's'}`
        : 'No security fields selected',
      { variant: ids.length > 0 ? 'success' : 'info' }
    );
    useGridCitationSelectionStore.getState().exit();
  };

  const handleGridSelectionCancel = () => {
    useGridCitationSelectionStore.getState().exit();
    setFilterCellIds([]);
  };

  // this might move down into the md viewer
  useEventSubscription(
    EventType.SPREADSHEET_ACTIVE_CELL_CHANGED,
    ([newCell, _prevCell]: [{ cellInfo: CapTableCellWithReferences }, CapTableCellWithReferences]) => {
      const cell = newCell.cellInfo;
      if (!citation || selectingCells()) {
        return;
      }

      // SCAL-8970: adding on top of a visible AI citation is a deliberate
      // choice - prompt for Append (the default) vs Replace, never decide
      // silently. Every other add appends exactly as before.
      if (shouldOfferReplace(cell.citationReferences, cell.alias, citation.documentReferenceId)) {
        setReplacePromptCell(cell);
        return;
      }

      completeManualCitationAdd(cell, 'append');
    }
  );

  // Update footnotes whenever citations in cells change
  useEffect(() => {
    registerCapTableCitationFootnotes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workbook?.captable?.cells]);

  // Cleanup footnotes on unmount
  useEffect(
    () => () => {
      clearFootnotes();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  // SCAL-8976: every document referenced by a cell's citations/mappings. These
  // back the per-card document name; resolved via the same documents-by-id query
  // the doc-view header uses, so names are reliable rather than best-effort.
  const referencedDocumentIds = useMemo(() => {
    const cells = workbook?.captable?.cells as Record<string, CapTableCellWithReferences> | undefined;
    if (!cells) {
      return [] as string[];
    }
    const ids = new Set<string>();
    Object.values(cells).forEach(cell => {
      [...(cell.citationReferences ?? []), ...(cell.mappingReferences ?? [])].forEach(ref => {
        if (ref.document_reference != null) {
          ids.add(String(ref.document_reference));
        }
      });
    });
    return Array.from(ids);
  }, [workbook?.captable?.cells]);

  // Resolve the cards' document names, but DEFER this batch until the browser is idle.
  // It fetches every referenced document in parallel; firing it on drawer open made those
  // N requests compete (browser caps ~6 connections/host) with the critical document-content
  // and tab queries, which left the preview/tabs intermittently blank until a window resize.
  // Deferring lets the document the user opens load first; the names fill in a beat later.
  const [docNamesEnabled, setDocNamesEnabled] = useState(false);
  useEffect(() => {
    const idleWindow = window as Window & {
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
      cancelIdleCallback?: (handle: number) => void;
    };
    if (idleWindow.requestIdleCallback) {
      const handle = idleWindow.requestIdleCallback(() => setDocNamesEnabled(true), { timeout: 2500 });
      return () => idleWindow.cancelIdleCallback?.(handle);
    }
    const handle = window.setTimeout(() => setDocNamesEnabled(true), 1200);
    return () => window.clearTimeout(handle);
  }, []);

  const { data: referencedDocuments } = useGetDocumentsById({
    documentIds: referencedDocumentIds,
    enabled: docNamesEnabled,
  });

  const documentNamesByRefId = useMemo(() => {
    const map = new Map<number, string>();
    Object.values(referencedDocuments ?? {}).forEach(doc => {
      if (doc.file?.filename) {
        map.set(doc.document_reference_id, doc.file.filename);
      }
    });
    return map;
  }, [referencedDocuments]);

  // Derive the "Pending Review" cards from the parsed cells and push them into the
  // review store. Re-runs when the resolved document names arrive so the cards'
  // documentNames fill in instead of rendering empty on first paint. Non-reviewers
  // (SCAL-9335) get no items at all, so every pending-count consumer stays inert.
  useEffect(() => {
    const cells = workbook?.captable?.cells as Record<string, CapTableCellWithReferences> | undefined;
    setReviewItems(isAiReviewer && cells ? aggregateReviewItems(cells, documentNamesByRefId) : []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workbook?.captable?.cells, documentNamesByRefId, setReviewItems, isAiReviewer]);

  // Each version starts with fresh selection, filters and default panel visibility.
  useEffect(() => {
    useGridCitationSelectionStore.getState().exit();
    resetReviewFilter();
  }, [selectedVersionId, resetReviewFilter]);

  // Leaving the sheet drops everything scoped to it - the grid selection and the
  // whole review session (items, filter, pane flag, reviewer stamp) - so a
  // different page's drawer never inherits them.
  useEffect(
    () => () => {
      useGridCitationSelectionStore.getState().exit();
      resetReviewSession();
    },
    [resetReviewSession]
  );

  // SCAL-8976 (fix #1): when the cap table loads with pending review items, open
  // the bottom drawer ONCE so the Pending Review panel is visible - for analysts
  // only (SCAL-9335). Guarded by a ref so it never re-opens after the user closes
  // it (expandDrawer is itself a no-op when the drawer is already open, so a
  // user-resized drawer is untouched).
  const autoOpenedRef = useRef(false);
  useEffect(() => {
    if (!isAiReviewer || autoOpenedRef.current || pendingReviewCount === 0) {
      return;
    }
    autoOpenedRef.current = true;
    publish(EventType.EXPAND_DRAWER, undefined);
  }, [isAiReviewer, pendingReviewCount, publish]);

  // SCAL-8976 perf: reflect a single accept/decline on the grid INCREMENTALLY. The
  // old path replaced the whole securities array (patchSecurities), which forced a
  // full ~5-6s grid re-parse and froze the UI after the card moved. Instead we update
  // only the affected cell - and, via the normal edit path, its formula dependents -
  // and keep the AI metadata flagged (accepted/dismissed) so the review aggregator
  // still derives the card into the Reviewed section. The securities store is left
  // untouched on purpose: the cells are the source of truth for the panel and the Save
  // reverse-parse, the backend already persisted the change, and the next fetch
  // reconciles. The AI-proposed security NAME needs column-level handling, so it keeps
  // the existing (full) path.
  // One cell per (security column, field alias). Field settles require the cell to
  // carry aiFieldMetadata (the callers guard); a citation settle only needs the
  // cell's citationReferences, so the lookup itself stays metadata-agnostic.
  const findReviewCell = useCallback(
    (securityId: number, fieldAlias: string) =>
      Object.values(
        (latestReviewState.current.workbook?.captable?.cells ?? {}) as Record<string, CapTableCellWithReferences>
      ).find(c => Number(c.columnId) === securityId && c.alias === fieldAlias),
    []
  );

  // WRITE-THROUGH: mirror an incremental settle onto the STORE security's
  // ai_finding metadata, deliberately IN PLACE (no dispatch, no rebuild). The
  // incremental path leaves the securities store stale on purpose (perf), but a
  // stale store is a landmine: ANY later rebuild-from-store (patchSecurities for
  // a settled NAME, deleteColumn, addSecurity) re-parses the grid from
  // security.ai_finding.field_metadata and RESURRECTS every settled field as
  // pending - which is exactly how the first bulk decline appeared to lose 73 of
  // 80 items (the settled-name patchSecurities rebuild wiped the rest). The
  // silent index write keeps the store consistent without triggering the 5-6s
  // re-parse the incremental path exists to avoid.
  const settleFieldOnStore = useCallback(
    (
      securityId: number,
      fieldAlias: string,
      action: 'accept' | 'decline' | 'reopen',
      options?: { settleCitations?: boolean }
    ) => {
      const securities = latestReviewState.current.captableInfo?.securities as SecurityModel[] | undefined;
      const idx = securities?.findIndex(s => s.id === securityId) ?? -1;
      if (securities && idx >= 0) {
        // Accept/reopen resolve underlying-security names against the sibling securities.
        if (action === 'accept') {
          securities[idx] = applyAcceptToSecurity(securities[idx], [fieldAlias], securities, options) as SecurityModel;
          return;
        }
        const applyToSecurity = {
          decline: applyDismissToSecurity,
          reopen: applyReopenToSecurity,
        }[action];
        securities[idx] = applyToSecurity(securities[idx], [fieldAlias], securities) as SecurityModel;
      }
    },
    []
  );

  // Accept-time pre-check shared by the single-card and bulk paths: the error for
  // a select-backed cell (e.g. underlying security) whose AI suggestion resolves
  // to no option - writing it would only be nulled by grid validation, so such a
  // finding must NOT be settled, server- or client-side. undefined = proceed.
  const acceptErrorFor = useCallback(
    (securityId: number, fieldAlias: string): string | undefined => {
      const cell = findReviewCell(securityId, fieldAlias);
      return cell?.aiFieldMetadata ? resolveAcceptValue(cell).error : undefined;
    },
    [findReviewCell]
  );

  const acceptFieldOnGrid = useCallback(
    (securityId: number, fieldAlias: string, options?: { settleCitations?: boolean }) => {
      const cell = findReviewCell(securityId, fieldAlias);
      if (!cell?.aiFieldMetadata) {
        return;
      }
      // Select-backed cells carry the option NAME in the AI metadata while the grid
      // stores the option ID: resolve BEFORE writing (shared resolver). Unresolvable
      // suggestions stay pending - callers pre-check via acceptErrorFor before the
      // server settle; this guard covers any path that did not.
      const { value: aiValue, error } = resolveAcceptValue(cell);
      if (error) {
        enqueueSnackbar(error, { variant: 'error' });
        return;
      }
      settleFieldOnStore(securityId, fieldAlias, 'accept', options);
      cell.aiFieldMetadata = { ...cell.aiFieldMetadata, status: 'accepted', is_accepted: true };
      // Default: the value-accept API also accepts linked AI citations. Skip when
      // this accept left citations pending for a later citation dismiss.
      if (options?.settleCitations !== false) {
        cell.citationReferences = acceptRefsForFields(cell.citationReferences, [fieldAlias]);
      }
      if (aiValue != null && String(aiValue) !== String(cell.value)) {
        // Apply the accepted value through the edit path so dependents (ownership %,
        // fully-diluted) recalc, but as a PROGRAMMATIC update so handleCellChanged does
        // NOT strip the metadata we just flagged. The same programmatic flag preserves the
        // cell's Excel mapping reference (a manual edit clears it), so color-by-reference
        // keeps the cell GREEN iff a mapping reference survives - accept never forces green
        // for a citation-only finding (SCAL-9152). The merge text auto-hides because the
        // value now equals the AI value. try/finally so the flag never stays stuck.
        captableSetProgrammaticUpdate?.(true);
        try {
          onTableChange(cell, aiValue as number);
        } finally {
          captableSetProgrammaticUpdate?.(false);
        }
      } else {
        cell.dispatchEvent?.();
      }
    },
    [findReviewCell, settleFieldOnStore, onTableChange, captableSetProgrammaticUpdate]
  );

  const dismissFieldOnGrid = useCallback(
    (securityId: number, fieldAlias: string) => {
      const cell = findReviewCell(securityId, fieldAlias);
      if (!cell?.aiFieldMetadata) {
        return;
      }
      // SCAL-9337: decide the value revert from the PENDING entry before the stamp.
      // A cell holding the AI value (purely AI-generated rows, accepted-then-
      // declined) reverts to the previous_value baseline - EMPTY when none, so
      // save validation forces an explicit entry; a merge suggestion never applied
      // its value, so the prior value already on the cell survives untouched.
      const { revert, value: revertValue } = resolveDeclineRevert(
        cell.aiFieldMetadata,
        cell.value,
        Array.isArray(cell.options) ? cell.options : undefined,
        fieldAlias
      );
      settleFieldOnStore(securityId, fieldAlias, 'decline');
      // Soft decline mirror of applyDismissToSecurity (SCAL-8976/9152): flag the metadata
      // dismissed and UNLINK the field's PDF citation through the shared declineRefsForFields
      // path (single source - no duplicated ref logic). The Excel mapping reference is
      // deliberately LEFT intact so color-by-reference keeps a prior-reference cell GREEN,
      // while a citation-only cell has no mapping reference and reverts to non-green.
      cell.aiFieldMetadata = { ...cell.aiFieldMetadata, status: 'dismissed', is_accepted: false };
      cell.citationReferences = declineRefsForFields(cell.citationReferences, [fieldAlias]);
      if (revert) {
        // Same programmatic edit path as the accept write: dependents recalc, the
        // just-stamped metadata and any mapping reference survive (a manual edit
        // would strip both). try/finally so the flag never stays stuck.
        captableSetProgrammaticUpdate?.(true);
        try {
          onTableChange(cell, (revertValue ?? '') as number);
        } finally {
          captableSetProgrammaticUpdate?.(false);
        }
      } else {
        cell.dispatchEvent?.();
      }
    },
    [findReviewCell, settleFieldOnStore, onTableChange, captableSetProgrammaticUpdate]
  );

  // SCAL-9262 reopen (un-accept / un-dismiss) grid mirror: flip the cell's
  // metadata + AI citation refs back to pending and, when un-accepting a cell
  // that still HOLDS the AI value, take the value back to the previous_value
  // baseline (the same resolveDeclineRevert rule as decline - a manual edit
  // made after acceptance survives). Returns whether the kept value should be
  // FLAGGED on the card: the entry was accepted, the cell holds some value, yet
  // it is not the AI value anymore, so the reopen left it untouched.
  const reopenFieldOnGrid = useCallback(
    (securityId: number, fieldAlias: string, wasAccepted: boolean): { valueKept: boolean } => {
      const cell = findReviewCell(securityId, fieldAlias);
      if (!cell?.aiFieldMetadata) {
        return { valueKept: false };
      }
      // Decide the revert from the SETTLED entry before the stamp (dismiss parity).
      const { revert, value: revertValue } = wasAccepted
        ? resolveDeclineRevert(
          cell.aiFieldMetadata,
          cell.value,
          Array.isArray(cell.options) ? cell.options : undefined,
          fieldAlias
        )
        : { revert: false, value: undefined };
      const valueKept =
        wasAccepted && !revert && cell.aiFieldMetadata.value != null && cell.value != null && cell.value !== '';
      settleFieldOnStore(securityId, fieldAlias, 'reopen');
      cell.aiFieldMetadata = { ...cell.aiFieldMetadata, status: 'pending', is_accepted: null };
      cell.citationReferences = reopenRefsForFields(cell.citationReferences, [fieldAlias]);
      if (revert) {
        // Programmatic edit path (accept/dismiss parity): dependents recalc, the
        // just-stamped metadata and any mapping reference survive.
        captableSetProgrammaticUpdate?.(true);
        try {
          onTableChange(cell, (revertValue ?? '') as number);
        } finally {
          captableSetProgrammaticUpdate?.(false);
        }
      } else {
        cell.dispatchEvent?.();
      }
      return { valueKept };
    },
    [findReviewCell, settleFieldOnStore, onTableChange, captableSetProgrammaticUpdate]
  );

  // CITATION-level settle (SCAL-8963) for reference-only review items: PATCH the
  // citation's review status, then mirror the settled status onto the cell's
  // citation refs (repaint) AND, in place, onto the store security's
  // citation_references - the same write-through discipline as settleFieldOnStore,
  // so a later rebuild-from-store re-derives the same settled state. The status
  // written mirrors the endpoint's action -> status mapping deterministically.
  const handleReviewSettleCitation = useCallback(
    async (item: ReviewItem, action: 'accept' | 'dismiss' | 'reopen') => {
      const citationId = item.primaryCitation?.id;
      if (citationId == null) {
        return;
      }
      try {
        await settleCitationReview({
          citationId,
          action,
          persist: capTableService.reviewCitation,
          getCell: () => findReviewCell(item.securityId, item.fieldAlias),
          getSecurities: () => latestReviewState.current.captableInfo?.securities,
          getReviewStore: useReviewStore.getState,
        });
      } catch (error) {
        enqueueSnackbar(`Failed to ${action} the citation`, { variant: 'error' });
        throw error;
      }
    },
    [capTableService, findReviewCell]
  );

  // MAPPING-level settle (SCAL-9667) for cards whose only evidence is AI Excel
  // mappings: PATCH every AI mapping row on the cell in ONE batch, then mirror the
  // settled status onto the cell's mapping refs (repaint) AND, in place, onto the
  // store security's mapping_references - the same write-through discipline as the
  // citation settle. The mapped value is never touched.
  const handleReviewSettleMapping = useCallback(
    async (item: ReviewItem, action: 'accept' | 'dismiss' | 'reopen') => {
      const mappingIds = item.mappingIds ?? [];
      if (mappingIds.length === 0) {
        return;
      }
      try {
        await settleMappingReview({
          mappingIds,
          action,
          persist: capTableService.reviewMappings,
          cell: findReviewCell(item.securityId, item.fieldAlias),
          patchSecurities,
        });
      } catch (error) {
        enqueueSnackbar(`Failed to ${action} the Excel mapping`, { variant: 'error' });
        throw error;
      }
    },
    [capTableService, findReviewCell, patchSecurities]
  );

  // The optional reviewer note from the card is forwarded as `reason` (persisted as
  // review_reason). The panel flips the card's status optimistically once these
  // resolve, MOVING it to the muted Reviewed section.
  const handleReviewAccept = useCallback(
    async (securityId: number, fieldAlias: string, reason?: string, options?: { settleCitations?: boolean }) => {
      if (fieldAlias === SECURITY_NAME_ALIAS) {
        await acceptField(securityId, fieldAlias, reason);
        return;
      }
      // Unresolvable select suggestion: fail BEFORE the server accept, so the
      // finding stays pending everywhere instead of settling with a lost value
      // (the throw keeps the panel card pending too).
      const unresolvedError = acceptErrorFor(securityId, fieldAlias);
      if (unresolvedError) {
        enqueueSnackbar(unresolvedError, { variant: 'error' });
        throw new Error(unresolvedError);
      }
      const request: AcceptAIFieldRequest = {
        field_names: [fieldAlias],
        ...(reason ? { reason } : {}),
        ...(options?.settleCitations === false ? { settle_citations: false } : {}),
      };
      try {
        await CapTablesService.apiCapTablesSecuritiesAcceptAiFieldsPartialUpdate(String(securityId), request);
      } catch (error) {
        enqueueSnackbar('Failed to accept the suggestion', { variant: 'error' });
        throw error;
      }
      acceptFieldOnGrid(securityId, fieldAlias, options);
    },
    [acceptField, acceptFieldOnGrid, acceptErrorFor]
  );

  // Keep the suggested value and dismiss the card's citation. Two persists:
  // value accept with citations left pending, then citation dismiss.
  const handleReviewAcceptValueRejectCitation = useCallback(
    async (item: ReviewItem) => {
      // The displayed primary citation may be human-created. Select the field's
      // AI citation before accepting changes its review status.
      const cell = findReviewCell(item.securityId, item.fieldAlias);
      const citation = pickActiveCitation(
        cell?.citationReferences?.filter(ref => isAiCreatedCitation(ref) && ref.status !== 'dismissed')
      );
      await handleReviewAccept(item.securityId, item.fieldAlias, undefined, { settleCitations: false });
      if (citation?.id != null) {
        // The field is already accepted: settle this citation by id on both the
        // cell and store, without re-running the pending-only field patch.
        await handleReviewSettleCitation({ ...item, primaryCitation: citation }, 'dismiss');
      }
    },
    [findReviewCell, handleReviewAccept, handleReviewSettleCitation]
  );

  const handleReviewDecline = useCallback(
    async (securityId: number, fieldAlias: string, reason?: string) => {
      if (fieldAlias === SECURITY_NAME_ALIAS) {
        await dismissField(securityId, fieldAlias, reason);
        return;
      }
      let securityDeleted = false;
      try {
        const response = await CapTablesService.apiCapTablesSecuritiesDismissAiFieldsPartialUpdate(String(securityId), {
          field_names: [fieldAlias],
          ...(reason ? { reason } : {}),
        });
        securityDeleted = Boolean(response.security_deleted);
      } catch (error) {
        enqueueSnackbar('Failed to decline the suggestion', { variant: 'error' });
        throw error;
      }
      if (securityDeleted) {
        // This was the AI-created security's LAST finding: the server deleted the
        // security itself. Drop it from the store and remove its column instead of
        // patching a field on the now-gone column.
        patchSecurities(securities => securities.filter(s => s.id !== securityId));
        dismissAiColumn(securityId);
        return;
      }
      dismissFieldOnGrid(securityId, fieldAlias);
    },
    [dismissField, dismissFieldOnGrid, patchSecurities, dismissAiColumn]
  );

  // SCAL-9262: reopen a settled card back to pending (undo of accept/decline).
  // Field-settleable items go through the reopen-ai-fields endpoint + the grid
  // mirror; reference-only items reopen their ACTIVE citation instead. Resolves
  // with the card's "Value kept" flag: un-accept never clobbers a manual edit
  // made after acceptance, it leaves the value and the panel flags the card.
  const handleReviewReopen = useCallback(
    async (item: ReviewItem): Promise<{ valueKept: boolean }> => {
      if (!item.canSettle) {
        await handleReviewSettleCitation(item, 'reopen');
        return { valueKept: false };
      }
      try {
        const { reopened_fields: reopened } = await capTableService.reopenAiFields(item.securityId, [item.fieldAlias]);
        if (!reopened.includes(item.fieldAlias)) {
          throw new Error(`Field '${item.fieldAlias}' could not be reopened`);
        }
      } catch (error) {
        enqueueSnackbar('Failed to reopen the review item', { variant: 'error' });
        throw error;
      }
      if (item.fieldAlias === SECURITY_NAME_ALIAS) {
        // Name findings live on the COLUMN: mirror through the securities store
        // patch (grid re-parse), like the settled-name accept/decline paths. The
        // name itself is never reverted (rejecting a name rejects the column).
        patchSecurities(
          securities =>
            securities.map(s =>
              s.id === item.securityId ? applyReopenToSecurity(s, [item.fieldAlias], securities) : s
            ) as SecurityModel[]
        );
        return { valueKept: false };
      }
      return reopenFieldOnGrid(item.securityId, item.fieldAlias, item.status === 'accepted');
    },
    [handleReviewSettleCitation, capTableService, patchSecurities, reopenFieldOnGrid]
  );

  // Bulk accept/decline the selected pending cards in ONE request, then reflect each
  // settled field on the grid incrementally (no full rebuild). Any settled NAME field
  // still needs the in-place security patch (column-level styling), so those are
  // collected and applied in a single patch - one rebuild only when names are present.
  const handleReviewBulkSettle = useCallback(
    async (action: 'accept' | 'decline', items: ReviewItem[], reason?: string): Promise<ReviewBulkResult> => {
      const capTableId = captableInfo?.id;
      // Accept-time resolution gate (parity with the single-card path): cards whose
      // select suggestion resolves to no option are EXCLUDED from the batch - the
      // server would settle them while the grid discards the value - and reported
      // through the same partial-failure summary as backend per-item errors. They
      // stay pending (not in settledIds, so the panel reverts them).
      const { acceptable, errors: unresolvedErrors } =
        action === 'accept'
          ? partitionAcceptableItems(items, item =>
            item.canSettle ? acceptErrorFor(item.securityId, item.fieldAlias) : undefined
          )
          : { acceptable: items, errors: [] };
      const fieldItems = acceptable.filter(item => item.canSettle);
      const citationItems = acceptable.filter(item => !item.canSettle && item.canSettleCitation);
      const mappingItems = acceptable.filter(isMappingSettleItem);
      const batchItems = buildBulkItems(new Set(fieldItems.map(i => i.id)), fieldItems);
      // Reference-only cards (citation or Excel mapping) settle outside the field batch.
      const referenceItemCount = citationItems.length + mappingItems.length;
      if (
        (capTableId == null && referenceItemCount === 0) ||
        (batchItems.length === 0 && referenceItemCount === 0 && unresolvedErrors.length === 0)
      ) {
        return { settledIds: [] };
      }

      try {
        const response =
          capTableId != null && batchItems.length > 0
            ? await CapTablesService.apiCapTablesAiReviewBatchPartialUpdate(String(capTableId), {
              action: action === 'accept' ? AIReviewBatchRequest.action.ACCEPT : AIReviewBatchRequest.action.DECLINE,
              items: batchItems,
              ...(reason ? { reason } : {}),
            })
            : undefined;
        const results = response?.results ?? [];

        // Fold the partial-success response: settled fields per security, the moved
        // card ids, and any per-item errors (see summarizeBulkResults).
        const { settledBySecurity, settledIds, errorMessages } = summarizeBulkResults(results);
        // AI-created securities this batch fully declined: the SERVER deleted them
        // and reports the ids - column removal is driven by this, never by a
        // settled-name heuristic (a declined name with pending siblings keeps its
        // column until the last finding is dismissed).
        const deletedSecurityIds = deletedSecurityIdsOf(response);

        const applyOnGrid = action === 'accept' ? acceptFieldOnGrid : dismissFieldOnGrid;
        let hasNameSettlement = false;
        settledBySecurity.forEach((fields, securityId) => {
          fields.forEach(fieldAlias => {
            if (fieldAlias === SECURITY_NAME_ALIAS) {
              hasNameSettlement = true;
            } else {
              applyOnGrid(securityId, fieldAlias);
            }
          });
        });

        // NAME columns keep the security patch (the only case that rebuilds). The
        // patch must apply ALL of this batch's settled fields - not just the names:
        // patchSecurities re-parses the whole grid from the securities store, so any
        // settled field left out of the patch would be rebuilt as PENDING again,
        // un-settling the rest of the batch on both the grid and the panel. (The
        // per-field settleFieldOnStore write-through already covers fields that went
        // through applyOnGrid; passing the full field list keeps this correct even
        // for fields the incremental path skipped - the settle helpers are idempotent.)
        if (hasNameSettlement || deletedSecurityIds.length > 0) {
          const deletedIdSet = new Set(deletedSecurityIds);
          patchSecurities(securities => {
            const settled = hasNameSettlement
              ? (applySettledFieldsToSecurities(securities, settledBySecurity, action) as SecurityModel[])
              : securities;
            // Server-deleted securities leave the store entirely (mirror of the
            // backend soft-delete), so grid syncs and refetches agree - and a
            // reopen-driven un-delete resurfaces them on refetch.
            return deletedIdSet.size > 0 ? settled.filter(s => s.id == null || !deletedIdSet.has(s.id)) : settled;
          });
          // Bulk parity with the single-card path (AIFieldAcceptContext.dismissField):
          // the whole column disappears only for the securities the SERVER deleted.
          deletedSecurityIds.forEach(dismissAiColumn);
        }

        const referenceAction = action === 'accept' ? 'accept' : 'dismiss';
        const citationSettledIds: string[] = [];
        const citationErrors: string[] = [];
        await Promise.all(
          citationItems.map(async item => {
            try {
              await handleReviewSettleCitation(item, referenceAction);
              citationSettledIds.push(item.id);
            } catch {
              citationErrors.push(`Failed to ${action} citation ${item.id}`);
            }
          })
        );

        const mappingSettledIds: string[] = [];
        const mappingErrors: string[] = [];
        await Promise.all(
          mappingItems.map(async item => {
            try {
              await handleReviewSettleMapping(item, referenceAction);
              mappingSettledIds.push(item.id);
            } catch {
              mappingErrors.push(`Failed to ${action} Excel mapping ${item.id}`);
            }
          })
        );

        const allErrors = [...errorMessages, ...unresolvedErrors, ...citationErrors, ...mappingErrors];
        if (allErrors.length > 0) {
          enqueueSnackbar(
            `${allErrors.length} item(s) could not be ${action === 'accept' ? 'accepted' : 'declined'}.`,
            { variant: 'warning' }
          );
          Logger.warn('CapTableWidgetDrawer - bulk review partial failure', allErrors);
        }

        return { settledIds: [...settledIds, ...citationSettledIds, ...mappingSettledIds] };
      } catch (error) {
        Logger.error('CapTableWidgetDrawer - bulk review failed', error);
        enqueueSnackbar(`Failed to ${action} the selected items`, { variant: 'error' });
        return { settledIds: [] };
      }
    },
    [
      captableInfo?.id,
      acceptErrorFor,
      acceptFieldOnGrid,
      dismissFieldOnGrid,
      patchSecurities,
      dismissAiColumn,
      handleReviewSettleCitation,
      handleReviewSettleMapping,
    ]
  );

  // Accept or reject the selection's pending suggestions through the review panel's bulk path.
  const handleGridSelectionSettle = (action: 'accept' | 'decline') =>
    runGridSelectionAction(async () => {
      const [verb, done] = action === 'accept' ? ['accept', 'Accepted'] : ['reject', 'Rejected'];
      const storeItems = useReviewStore.getState().reviewItems;
      const items = pendingReviewItemsForSelection(
        selectedCapTableCells(),
        storeItems.length > 0
          ? storeItems
          : aggregateReviewItems(workbook.captable.cells as Record<string, CapTableCellWithReferences>)
      );
      if (items.length === 0) {
        enqueueSnackbar(`No pending suggestions to ${verb}`, { variant: 'info' });
        return;
      }
      const { settledIds } = await handleReviewBulkSettle(action, items);
      if (settledIds.length > 0) {
        enqueueSnackbar(`${done} suggestions on ${settledIds.length} cell${settledIds.length === 1 ? '' : 's'}`, {
          variant: 'success',
        });
      }
    });

  // SCAL-8976 (Feature B): the card's eyeball icon reveals its PRIMARY source
  // citation in the PDF viewer, highlighted by the citation's REVIEW STATUS
  // (pending -> AI-purple outline, reviewed -> plain yellow; SCAL-8986). Mirrors
  // CitationValueViewer.onView (setActiveCitation + NAVIGATE_TO_DOCUMENT_REFERENCE).
  // NO-OP for items with no document-backed citation.
  const handleReviewRevealPdf = useCallback(
    (item: ReviewItem) => {
      const jump = buildReviewDocJump(item);
      if (!jump) {
        return;
      }
      setActiveCitation(jump.activeCitation, jump.variant);
      publish(EventType.NAVIGATE_TO_DOCUMENT_REFERENCE, jump.navigatePayload);
    },
    [setActiveCitation, publish]
  );

  // SCAL-8976 (Feature C): a review card body becoming active reveals its PRIMARY
  // Excel mapping-reference source - open the xlsx viewer to the mapped sheet and
  // highlight the mapped cell(s) via NAVIGATE_TO_DOCUMENT_REFERENCE. Unlike the
  // eyeball PDF jump it does NOT setActiveCitation. NO-OP for items with no Excel
  // mapping reference (buildReviewExcelJump returns null).
  const handleReviewRevealExcel = useCallback(
    (item: ReviewItem) => {
      const jump = buildReviewExcelJump(item);
      if (!jump) {
        return;
      }
      publish(EventType.NAVIGATE_TO_DOCUMENT_REFERENCE, jump.navigatePayload);
    },
    [publish]
  );

  // Workbook-toolbar clear: Excel mapping references only. Citations stay put,
  // because a save would persist their removal as plain human dismissals; the
  // AI Processing reset dismisses them with the marker a rerun can revive.
  const clearCellReferences = (docRefPayload?: ClearAttributeMappingsPayload) => {
    const isCleared = (ref: MappingReferenceData) =>
      !docRefPayload ||
      (ref.document_reference === docRefPayload.documentReferenceId &&
        (!docRefPayload.sheet || ref.sheet_name === docRefPayload.sheet));

    const changed: CapTableCellWithReferences[] = [];
    Object.values(workbook.captable.cells as CapTableCellWithReferences[]).forEach(cell => {
      const mappings = cell.mappingReferences ?? [];
      const kept = mappings.filter(ref => !isCleared(ref));
      // "Clear all" also drops the column-level document references.
      const dropsColumnar =
        !docRefPayload && Boolean(cell.columnarReferences?.length || cell.columnarReferencesMetadata?.length);
      if (kept.length === mappings.length && !dropsColumnar) {
        return;
      }

      cell.mappingReferences = kept;
      if (!kept.length) {
        cell.columnarReferences = [];
        cell.columnarReferencesMetadata = [];
        cell.className = removeClassName(cell.className ?? '', 'cell-with-references');
      }
      changed.push(cell);
    });
    changed.forEach(cell => onTableChange(cell, cell.value as number));

    // Recalculate title cells for affected columns
    if (docRefPayload && changed.length > 0) {
      const referenceGeneratorService = new CapTableReferenceGenerator();
      const affectedColumns = new Set<string>();

      changed.forEach(({ columnLegend }) => {
        if (columnLegend) {
          affectedColumns.add(columnLegend);
        }
      });

      affectedColumns.forEach(columnLegend => {
        const capCell = workbook.captable.cells[`${columnLegend}1`] as CapTableCellWithReferences;
        if (capCell) {
          const columnMappingReferences: MappingReferenceData[] = [];
          Object.keys(workbook.captable.cells).forEach(key => {
            if (key.startsWith(columnLegend) && key !== `${columnLegend}1`) {
              const cell = workbook.captable.cells[key] as CapTableCellWithReferences;
              if (cell.mappingReferences && cell.mappingReferences.length > 0) {
                columnMappingReferences.push(...cell.mappingReferences);
              }
            }
          });
          const docRefsData = referenceGeneratorService.getDocumentReferences(columnMappingReferences);
          capCell.columnarReferences = docRefsData.documentIds;
          capCell.columnarReferencesMetadata = docRefsData.metadata;
          capCell.dispatchEvent();
        }
      });
    }
  };

  const captableIndex = (alias: string) =>
    rowConfig({
      isDisabled: false,
      measurementDate: '',
      customTitles: {},
      firmName: '',
    }).findIndex(item => item.alias === alias);

  const pushAttributeMappingsToCapTable = async (payload?: HandlePushingAttributeMappingsPayload) => {
    const mapper = getAttributeMapperManager();
    if (!mapper) {
      return;
    }

    // Set flag to indicate programmatic updates are happening
    captableSetProgrammaticUpdate?.(true);

    try {
      const securityFields = rowConfig({ isDisabled: false, measurementDate: '', customTitles: {} }).map(
        item => item.alias
      );
      const mappingService = new CapTableReferenceGenerator(securityFields);

      // find which cell is in an expr state
      const cellsInExprState = Object.values(workbook.captable.cells as CapTableCellWithReferences[]).filter(
        cell => cell.expr === '='
      );

      if (!cellsInExprState.length) {
        return;
      }

      if (!payload?.documentReferencePayload) {
        return;
      }

      cellsInExprState.forEach(cell => {
        const titleCellForColumn = workbook.captable.cells[`${cell.columnLegend}1`] as CapTableCellWithReferences;

        // need to clear similar xValue and yValue pairs from the mapper
        const expectedXValue = cell.alias;
        const expectedYValue = titleCellForColumn.value;

        // for cap table each single cell selected in the table must only be represented by one value below
        mapper.removeSingleCellByXYValue({
          xValue: expectedXValue,
          yValue: expectedYValue,
        });

        mapper.handleSingularAddition(payload.documentReferencePayload!, {
          // coordinates should be the cell in the drawer sheet
          coordinates: `${payload.selectedCell?.cellInfo.key}`,

          // these are the source values
          xValue: cell.alias,
          yValue: titleCellForColumn.value,
          direction: 'positive',
        });
      });

      const mapping = getCellMapping();
      const evaluatedAttributeData = mapper.calculateValues(mapping);

      // Get the sheet attribute mapping which contains the reference data
      const { sheetAttributeMapping } = mapper;

      // Generate mapping references from the sheet attribute data (not evaluated data)
      const generatedReferences = mappingService.generateMappingReferences(sheetAttributeMapping.data);

      const captableChangeSet: CellChange[] = [];

      // clearCellReferences();

      Object.keys(evaluatedAttributeData).forEach(columnarMetric => {
        Object.keys(evaluatedAttributeData[columnarMetric]).forEach(rowMetric => {
          const captableRowIndex = captableIndex(rowMetric);

          if (captableRowIndex >= 0) {
            const updatedCell = Object.values(workbook.captable.cells as CapTableCellWithReferences[]).find(
              (item: CapTableCellWithReferences) => {
                // Only consider cells that are in mapping mode (expr === '=')
                if (item.expr !== '=') {
                  return false;
                }

                const titleColumn = workbook.captable.cells[`${item.columnLegend}1`];
                if (!titleColumn) {
                  return false;
                }

                return item.alias === rowMetric && titleColumn.value === columnarMetric;
              }
            ) as CapTableCellWithReferences | undefined;
            if (updatedCell) {
              const mappedValue = evaluatedAttributeData[columnarMetric][rowMetric].value;

              // // Attach reference metadata to the cell
              const referenceMetadata = mappingService.getReferenceMetadataForCell(
                columnarMetric,
                rowMetric,
                generatedReferences
              );

              // // Add mapping reference metadata to cell
              const titleCell = workbook.captable.cells[`${updatedCell.columnLegend}1`];

              if (referenceMetadata) {
                updatedCell.mappingReferences = applyProposedExcelReferences(
                  updatedCell.mappingReferences,
                  referenceMetadata
                );
                const columnarRefs = mappingService.getDocumentReferences(referenceMetadata);

                // set col refs on column
                titleCell.columnarReferences = columnarRefs.documentIds;
                titleCell.columnarReferencesMetadata = columnarRefs.metadata;

                captableChangeSet.push({
                  cell: titleCell,
                  value: titleCell.value,
                });
              } else {
                updatedCell.mappingReferences = [];
                titleCell.columnarReferences = [];
                titleCell.columnarReferencesMetadata = [];
              }

              // here add in the cell refs for the col

              captableChangeSet.push({
                cell: updatedCell,
                value: mappedValue,
              });
            }
          }
        });
      });

      const fullChanges = captableChangeSet;
      fullChanges.forEach(change => {
        onTableChange(change.cell, change.value);
      });

      if (payload?.showNotification) {
        enqueueSnackbar('Mapping pushed to captable', { variant: 'success' });
      }
    } finally {
      // Reset flag after all changes are complete
      captableSetProgrammaticUpdate?.(false);
    }
  };

  const handleClearMappings = (docRefPayload?: ClearAttributeMappingsPayload) => {
    clearCellReferences(docRefPayload);
    clearAttributeMapperManager(docRefPayload);
    pushAttributeMappingsToCapTable(undefined);

    registerCapTableCitationFootnotes();
  };

  const filterRows = row => !row.readOnly;
  const mapRowsToOptions = row => ({ label: row.value, value: row.alias });

  const attributeReferenceConfig = useMemo(() => {
    if (!captableInfo) {
      return { columns: [], rows: [], selectionMode: AttributeMappingType.SINGLE_CELL };
    }

    const columns = captableInfo.securities?.map((security: SecurityModel) => ({
      label: security.name,
      value: security.id,
    }));

    const captableRows = rowConfig({
      isDisabled: false,
      measurementDate: '',
      customTitles: {},
      firmName: '',
    });
    const captableOptions = captableRows.filter(filterRows).map(mapRowsToOptions);
    const rows = [{ label: 'CapTable', value: null, isTitle: true }, ...captableOptions];
    return {
      columns,
      rows,
      selectionMode: AttributeMappingType.SINGLE_CELL,
    };
  }, [captableInfo]);

  const handleCitationPushed = (payload: CitationPushedPayload, documentReferenceId: number) => {
    setCitation({ ...payload, documentReferenceId });
    const selectMultipleAction = isAiReviewer
      ? () => (
        <Button
          color="inherit"
          size="small"
          onClick={() => useGridCitationSelectionStore.getState().enterAddMultiple()}>
            Select multiple
        </Button>
      )
      : undefined;
    enqueueSnackbar('Click a cell to add this citation', { action: selectMultipleAction });
  };

  const handleReprocessClick = useCallback(
    (documentClicked: DocumentReferencesView) => {
      setReprocessJobMetadata({
        document_reference_id: documentClicked.document_reference_id,
        measurement_date_id: currentMeasurementDate?.id,
        file_id: documentClicked.file.id,
      });
      setReprocessDialogOpen(true);
    },
    [currentMeasurementDate?.id]
  );

  const handleReprocessCancel = () => {
    setReprocessJobMetadata(null);
    setReprocessDialogOpen(false);
  };

  const handleReprocessConfirm = async () => {
    setReprocessDialogOpen(false);
    if (!reprocessJobMetadata || reprocessJobMetadata.file_id == null) {
      enqueueSnackbar('No document information available to reprocess', { variant: 'error' });
      return;
    }
    if (!companyInfo?.id) {
      enqueueSnackbar('Company information not available', { variant: 'error' });
      return;
    }

    const companyIdStr = companyInfo.id.toString();
    const fileId = reprocessJobMetadata.file_id;

    try {
      const newJob = await ApiService.apiCompanyDocumentGenerationJobsNewCreate(companyIdStr, {
        processing_type: ApiCreateDocumentGenerationJob.processing_type.CAP_TABLE,
        reference_files: [fileId.toString()] as any,
        desired_termination_state: 'COMPLETE',
        parser_overrides: [
          {
            file_type: 'xlsx',
            xlsx_parsing_mode: 'JSON_CONVERSION',
            minify_json: true,
          } as any,
        ],
        metadata: {
          document_reference_id: reprocessJobMetadata.document_reference_id,
          measurement_date_id: reprocessJobMetadata.measurement_date_id,
          file_id: fileId,
        },
      } as any);

      Logger.info('CapTableWidgetDrawer:', `Adding cap table job ${newJob.job_id} to polling`);

      addJob({
        id: newJob.job_id,
        companyId: companyIdStr,
        details: {
          title: CAP_TABLE_REPROCESS_JOB_TITLE,
          description: `Reprocessing document ${reprocessJobMetadata.document_reference_id}`,
        },
        pollingConfig: {
          pollingFunction: async () => CompanyService.apiCompanyDocumentGenerationJobsRead(companyIdStr, newJob.job_id),
          pollingInterval: 3000,
          pollingArgs: [],
        },
        // Same terminal handling as the Process flow: apply, announce the count or
        // warn honestly when nothing was extracted, stay silent on already_applied.
        // The grid refresh rides the published completion event, consumed by the
        // cap-table page root (useCapTableGenerationRefresh) so it fires no matter
        // which drawer tab is active.
        onResult: createCapTableGenerationOnResult({
          companyId: companyIdStr,
          logContext: 'CapTableWidgetDrawer',
          publish,
          removeJob,
        }),
        onError: (error: Error) => {
          Logger.error('CapTableWidgetDrawer - Reprocess job polling error', error);
        },
      });

      enqueueSnackbar('Reprocessing started. The cap table will update when the job completes.', {
        variant: 'info',
      });
    } catch (error: any) {
      Logger.error('CapTableWidgetDrawer - Error reprocessing document', error);
      const errorMessage = error?.body?.detail || error?.message || 'Failed to reprocess document';
      enqueueSnackbar(`Error: ${errorMessage}`, { variant: 'error' });
    } finally {
      setReprocessJobMetadata(null);
    }
  };

  const drawerSelection = documentReferencesProps as
    | { referencedFeatureId?: number | string; selectedMeasurementDate?: { id?: number }; currentPage?: string }
    | undefined;
  // The version selector's id first: the store's cap table is blanked while a load runs.
  const selectedCapTableId = Number(selectedVersionId ?? drawerSelection?.referencedFeatureId) || null;
  const selectedMeasurementDateId = drawerSelection?.selectedMeasurementDate?.id ?? currentMeasurementDate?.id ?? null;
  // Identity for late replies: company, date and version, none of which a load blanks.
  const guardSelection = useSelectionGuard(
    `${companyInfo?.id ?? null}:${selectedMeasurementDateId}:${selectedVersionId ?? null}`
  );

  // Names the table in the toast: a slow refresh must never leave which one was cleared in doubt.
  const selectedCapTableLabel = [
    companyInfo?.name,
    captableInfo?.is_primary === false ? captableInfo?.name : null,
    currentMeasurementDate?.name,
  ]
    .filter(Boolean)
    .join(' - ');

  // The clear actions match the server: staff with edit rights on this company, or a superuser.
  const canClearCapTable = useCanClearCapTable(companyInfo?.id);

  // A reply that lands after the analyst moved on must not overwrite the new selection.
  const refreshCapTable = (targetCapTableId: number, stillCurrent: () => boolean) =>
    (getCapTableInfo as (id?: number, update?: boolean, force?: boolean, shouldApply?: () => boolean) => Promise<void>)(
      targetCapTableId,
      true,
      true,
      stillCurrent
    );

  const handleConfirmReset = async (mode: CapTableResetMode) => {
    const capTableId = captableInfo?.id;
    const stillCurrent = guardSelection();
    if (!capTableId) {
      enqueueSnackbar('Cap table is not loaded', { variant: 'error' });
      return false;
    }
    try {
      const result = await CapTablesService.apiCapTablesAiEvidenceResetCreate(String(capTableId), {
        mode: CAP_TABLE_RESET_API_MODE[mode] as AIEvidenceResetRequest.mode,
      });
      await refreshCapTable(capTableId, stillCurrent);
      enqueueSnackbar(capTableResetSummary(mode, result), { variant: 'success' });
      return true;
    } catch (error) {
      Logger.error('CapTableWidgetDrawer - reset failed', error);
      enqueueSnackbar(extractResponseErrorMessage(error) ?? 'Failed to complete the reset', { variant: 'error' });
      return false;
    }
  };

  const handleClearCapTable = async (targetCapTableId: number, targetLabel: string) => {
    const stillCurrent = guardSelection();
    await runCapTableClear(targetCapTableId, {
      clearCapTable: id => capTableService.clearCapTable(id),
      refresh: () => refreshCapTable(targetCapTableId, stillCurrent),
      notify: (message, variant) => enqueueSnackbar(message, { variant }),
      targetLabel,
      isStillSelected: stillCurrent,
    });
  };

  const canSettleSelection = gridSelectionMode === 'add-multiple' && selectedCapTableCells().some(cellHasPendingSuggestion);

  return (
    <>
      {isAiReviewer &&
        gridSelectionMode === 'add-multiple' &&
        gridSelectionToolbarHost &&
        createPortal(
          <GridCitationSelectionToolbar
            count={gridSelected.length}
            canSettle={canSettleSelection}
            hasStagedCitation={Boolean(citation)}
            busy={gridCitationBusy}
            onAttach={handleGridSelectionAttach}
            onSettle={handleGridSelectionSettle}
            onClear={handleGridSelectionClear}
            onFilterDrawer={handleGridSelectionFilter}
            onCancel={handleGridSelectionCancel}
          />,
          gridSelectionToolbarHost
        )}
      <AttributeMappedWidgetDrawer
        documentReferenceHeaderContent={
          <GenerateCapTableJob
            trigger={
              <AiProcessingMenu
                onGenerateCapTable={() => setGenerateDialogOpen(true)}
                onCreateCitations={createCitationsAction.isAvailable ? createCitationsAction.openDialog : undefined}
                createCitationsDisabledReason={createCitationsAction.disabledReason}
                onConfirmReset={handleConfirmReset}
                canClear={canClearCapTable}
                capTableId={selectedCapTableId}
                capTableLabel={selectedCapTableLabel}
                onClearCapTable={handleClearCapTable}
                onMenuOpenChange={open => {
                  if (open) {
                    setCreateCitationsEnabled(true);
                  }
                }}
              />
            }
            dialogOpen={generateDialogOpen}
            onDialogOpenChange={setGenerateDialogOpen}
            selectedMeasurementDate={drawerSelection?.selectedMeasurementDate}
            currentPage={drawerSelection?.currentPage}
            referencedFeatureId={drawerSelection?.referencedFeatureId}
          />
        }
        documentApplicationStorageKey={documentApplicationStorageKey}
        attributeMapper={attributeMapperManager}
        attributeReferenceConfig={attributeReferenceConfig}
        onCitationPushed={handleCitationPushed}
        onReprocessDocument={handleReprocessClick}
        applicationContext={applicationContext}
        documentReferencesProps={documentReferencesProps}
        notesProps={notesProps}
        spreadsheetNotesProps={spreadsheetNotesProps}
        handlePushingAttributeMappings={pushAttributeMappingsToCapTable}
        handleClearMappings={handleClearMappings}
        sidePanel={
          !isAiReviewer || reviewPaneCollapsed ? undefined : (
            <ReviewSidePanel
              onAccept={handleReviewAccept}
              onDecline={handleReviewDecline}
              onAcceptValueRejectCitation={handleReviewAcceptValueRejectCitation}
              onSettleCitation={handleReviewSettleCitation}
              onSettleMapping={handleReviewSettleMapping}
              onReopen={handleReviewReopen}
              onBulkSettle={handleReviewBulkSettle}
              onRevealPdf={handleReviewRevealPdf}
              onRevealExcel={handleReviewRevealExcel}
              documentNamesByRefId={documentNamesByRefId}
            />
          )
        }
        headerLeading={isAiReviewer ? <PendingReviewBadge /> : undefined}
      />
      <CreateCapTableCitationsDialog
        documents={createCitationsAction.documents}
        onClose={createCitationsAction.closeDialog}
        open={createCitationsAction.isDialogOpen}
      />

      {/* SCAL-8970: the target cell already shows an AI citation. Appending
          (keeping both) is the default; replacing - which soft-dismisses the
          AI citation on save - must be an explicit choice. Cancel keeps the
          pushed citation pending so another cell can still be picked. */}
      <Dialog open={replacePromptCell !== null} onClose={() => setReplacePromptCell(null)}>
        <DialogTitle>Cell already has an AI citation</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This cell already has an AI-generated citation. Keep both citations, or replace the AI citation with your
            manual one? Replacing dismisses the AI citation when the cap table is saved.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReplacePromptCell(null)} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={() => replacePromptCell && completeManualCitationAdd(replacePromptCell, 'replace')}
            color="inherit">
            Replace AI citation
          </Button>
          <Button
            onClick={() => replacePromptCell && completeManualCitationAdd(replacePromptCell, 'append')}
            color="primary"
            variant="contained"
            autoFocus>
            Keep both
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={reprocessDialogOpen} onClose={handleReprocessCancel}>
        <DialogTitle>Reprocess Cap Table Document</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will run a new AI cap table processing job on this document. When it completes, the AI results will be
            applied to the cap table and existing securities will be replaced.
            <br />
            <br />
            Do you want to continue?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleReprocessCancel} color="inherit">
            Cancel
          </Button>
          <Button onClick={() => handleReprocessConfirm()} color="primary" variant="contained">
            Reprocess
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CapTableWidgetDrawer;
